import { LEGACY_SCALE, imageToView, viewToImage } from './formSetupCoords';

export const camelCaseToTitle = camelCase => {
  if (camelCase) {
    return camelCase
      .replace(/([A-Z])/g, match => ` ${match}`)
      .replace(/^./, match => match.toUpperCase())
      .trim();
  }
  return camelCase;
};

export const titleToCamelCase = str => {
  return str
    .split(' ')
    .map((word, index) => {
      // If it is the first word make sure to lowercase all the chars.
      if (index === 0) {
        return word.toLowerCase();
      }
      // If it is not the first word only upper case the first char and lowercase the rest.
      return (
        word.charAt(0).toUpperCase() +
        word.slice(1).toLowerCase()
      );
    })
    .join('');
};

export const titleToSnakeCase = string => {
  return string
    .replace(/\d+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map(word => word.toLowerCase())
    .join('_');
};

export const camelCaseToFieldName = camelCase => {
  if (camelCase) {
    const resultArray = [];
    const array = camelCase
      .replace(/([A-Z])/g, match => ` ${match}`)
      .trim()
      .split(' ');
    array.forEach((el, index) => {
      if (index === 0) {
        resultArray.push(
          `${el.charAt(0).toUpperCase()}${el.slice(1)}`,
        );
      } else {
        resultArray.push(
          `${el.charAt(0).toLowerCase()}${el.slice(1)}`,
        );
      }
    });
    return resultArray.join(' ');
  }
  return camelCase;
};

const createMarkupFromFields = (
  model,
  markup,
  parents,
  data,
  isModel = true,
) => {
  const mutableParents = [...parents];
  let isNeedToAdd = true;
  const newObject = {
    field: camelCaseToFieldName(mutableParents[0]),
    nested_fields: [],
    coordinates: [],
    box_size: [],
  };
  markup.forEach(node => {
    if (
      node?.field ===
      camelCaseToFieldName(mutableParents[0])
    ) {
      isNeedToAdd = false;
      mutableParents.shift();
      createMarkupFromFields(
        model,
        node.nested_fields,
        mutableParents,
        data,
        false,
      );
    }
  });
  if (isNeedToAdd) {
    if (isModel) {
      newObject.model = model;
    }
    if (parents.length > 1) {
      mutableParents.shift();
      createMarkupFromFields(
        model,
        newObject.nested_fields,
        mutableParents,
        data,
        false,
      );
    } else {
      const keys = Object.entries(data);
      keys.forEach(key => {
        newObject[key[0]] = data[key[0]];
      });
    }
    markup.push(newObject);
  }
};

// Markup key carrying a Super Admin's test value. Mirrors PREVIEW_TEXT_KEY in
// form_setup/helpers.py, where only the PREVIEW renderer reads it.
export const PREVIEW_TEXT_KEY = 'preview_text';

// Markup keys for a placeholder that wraps. Mirror MULTILINE_KEY / MAX_LINES_KEY
// in form_setup/helpers.py.
//
// snake_case deliberately: `form_markup` is on utils/opaqueKeys.ts, so nothing
// inside it is case-converted in either direction — the key written here is the
// key Django reads, verbatim.
//
// Written ONLY when wrapping is on. A placeholder that has never been switched
// on emits exactly the keys it always did, so re-saving an existing form cannot
// change a single byte of how it prints.
export const MULTILINE_KEY = 'multiline';
export const MAX_LINES_KEY = 'max_lines';

// Markup key for a placeholder that prints at its OWN size. Mirrors
// FONT_SIZE_KEY in form_setup/helpers.py, and snake_case for the same reason as
// the two above.
//
// The Font Size picker in the field panel sets the whole form, which is exactly
// the problem: Information is the one value long enough to need a small size,
// and turning the form down to suit it took the permit number and the dates
// with it. Written ONLY when a size is picked, so a form that never used it
// keeps printing every placeholder at the form's size.
export const FONT_SIZE_KEY = 'font_size';

// Mirrors DEFAULT_MAX_LINES in form_setup/pdf_render.py — what the renderer
// falls back to when the markup carries no max_lines.
export const DEFAULT_MAX_LINES = 3;

/**
 * The Max lines a wrapped placeholder actually gets.
 *
 * The panel holds Max lines as the raw string its input produced, so blank,
 * '0' and junk all have to mean "the default" rather than zero lines — zero
 * would clip every value to nothing. Deliberately the ONE implementation:
 * the number that sizes the box on the canvas has to be the number written
 * into the markup, or the two disagree by a whole line.
 */
export const resolveMaxLines = (value: any): number =>
  Math.max(1, Number(value) || DEFAULT_MAX_LINES);

/**
 * A placeholder's own font size in raster pixels, or 0 for "use the form's".
 *
 * The panel holds it as the raw string its <select> produced, and the "Same as
 * form" option is the empty string. 0 is the same answer as blank on purpose:
 * it is what the server treats as absent (`_node_fontsize`), so the two cannot
 * disagree about which size a placeholder prints at.
 */
export const resolveFontSize = (value: any): number => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : 0;
};

/**
 * Build the markup tree the server stores.
 *
 * `includePreviewText` is opt-in and defaults OFF, so the save path cannot
 * emit test values even by accident — only the Preview PDF call turns it on.
 * That is the whole safety story for the feature: a value typed to test a
 * layout has no route into a saved form or an issued document.
 */
export const createResult = (
  activeFields,
  model,
  scale: number = LEGACY_SCALE,
  includePreviewText = false,
) => {
  const result = [];
  activeFields.forEach(field => {
    const parents = [...field.parents];
    parents.shift();
    // Re-emit an untouched field's stored numbers verbatim. Both directions
    // round, so recomputing them from the on-screen position drifts a pixel at
    // a time on every open/save cycle ([184,720] -> [184,721] -> ...).
    // `stored` is cleared the moment the field is actually dragged or resized.
    createMarkupFromFields(model, result, parents, {
      coordinates: field.stored
        ? [...field.stored.coordinates]
        : [viewToImage(field.x, scale), viewToImage(field.y, scale)],
      box_size: field.stored
        ? [...field.stored.boxSize]
        : [viewToImage(field.width, scale), viewToImage(field.height, scale)],
      // Max lines is held as the raw string the panel's input produced, so an
      // empty box means "use the default" rather than 0 lines.
      ...(field.multiline
        ? {
          [MULTILINE_KEY]: true,
          [MAX_LINES_KEY]: resolveMaxLines(field.maxLines),
        }
        : {}),
      // Only when a size was actually picked. "Same as form" writes no key at
      // all rather than the form's current number, so the placeholder keeps
      // following the Font Size picker instead of being frozen at whatever it
      // happened to read on the day it was saved.
      ...(resolveFontSize(field.fontSize)
        ? { [FONT_SIZE_KEY]: resolveFontSize(field.fontSize) }
        : {}),
      ...(includePreviewText && field.previewText
        ? { [PREVIEW_TEXT_KEY]: field.previewText }
        : {}),
    });
  });
  return result;
};

/**
 * Read a markup node's nested fields and box size under EITHER spelling.
 *
 * `form_markup` is on utils/opaqueKeys.ts, so the axios interceptor no longer
 * case-converts anything inside it — the value arrives exactly as Django stored
 * it, with `nested_fields` and `box_size`. That list exists for the Inspection
 * Form Builder, whose markup keys are field labels a Super Admin typed and must
 * survive verbatim. Form Setup's markup happens to live under the same key but
 * is a completely different shape: its keys ARE API names, and this code was
 * written when they still arrived camelCased.
 *
 * The result was silent, and worse than it looks:
 *   - `boxSize` undefined -> width/height 0 -> clamped to MIN_CHIP_W/H, so
 *     every placeholder's Width read 40 and Height 20 no matter what was
 *     saved. Typing 300 saved correctly (box_size [833, 56] in the DB) and
 *     came back 40 on the next open, which is the reported bug.
 *   - `nestedFields` undefined -> every branch node looked like a leaf, so a
 *     tree like `Inspected inspections > Permit > Number` collapsed to a single
 *     chip at [0, 0] and saving wrote that collapse back, DELETING the nested
 *     placeholders.
 *
 * Both spellings are accepted rather than one being picked, because the same
 * markup reaches this code through paths that do and do not convert (RTK Query
 * and axios do not share a converter), and a tolerant read cannot be wrong in
 * either direction.
 */
const nestedOf = (field: any): any[] =>
  field?.nestedFields || field?.nested_fields || [];

const boxSizeOf = (field: any): any[] =>
  field?.boxSize || field?.box_size || [];

// Same tolerant read as nestedOf / boxSizeOf, and for the same reason: the
// markup reaches this code through paths that do and do not case-convert.
const maxLinesOf = (field: any): string => {
  const value = field?.max_lines ?? field?.maxLines;
  return value === undefined || value === null ? '' : `${value}`;
};

const fontSizeOf = (field: any): string => {
  const value = field?.font_size ?? field?.fontSize;
  return value === undefined || value === null ? '' : `${value}`;
};

export const getActiveFieldsFromMarkup = (
  field,
  parents = ['fields'],
  result,
  scale: number = LEGACY_SCALE,
) => {
  // `parents` arrives ALREADY camelCased from the caller, so only the new
  // segment gets converted here.
  //
  // This used to re-map the whole array on every level. titleToCamelCase is not
  // idempotent — it lowercases each space-separated word, so a second pass over
  // 'inspectedInspections' sees a single word and returns
  // 'inspectedinspections'. createResult then emitted the field name
  // 'Inspectedinspections' instead of 'Inspected inspections', which
  // form_setup/validators.py cannot resolve: the whole PATCH 400'd and the
  // user's placeholder moves were silently discarded. Every Reports form was
  // affected, since all of their placeholders hang off 'Inspected inspections'.
  const parentsArray = [...parents, titleToCamelCase(field?.field)];

  const nested = nestedOf(field);

  if (nested.length) {
    nested.forEach(el => {
      getActiveFieldsFromMarkup(el, parentsArray, result, scale);
    });
  } else {
    const coordinates = field?.coordinates || [];
    const boxSize = boxSizeOf(field);
    result.push({
      parents: parentsArray,
      x: imageToView(coordinates[0], scale),
      y: imageToView(coordinates[1], scale),
      width: imageToView(boxSize[0], scale),
      height: imageToView(boxSize[1], scale),
      multiline: !!field?.[MULTILINE_KEY],
      maxLines: maxLinesOf(field),
      fontSize: fontSizeOf(field),
      // Exactly what the server sent, so an untouched field round-trips
      // unchanged. See createResult.
      stored: {
        coordinates: [Number(coordinates[0]) || 0, Number(coordinates[1]) || 0],
        boxSize: [Number(boxSize[0]) || 0, Number(boxSize[1]) || 0],
      },
      value: titleToSnakeCase(
        parentsArray[parentsArray?.length - 1],
      ),
    });
  }
};
